import React from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Phone, MapPin, Clock, Loader2, CheckCircle2, ChevronDown, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { PublicNav, PublicFooter } from "../components/PublicChrome";

const ROLES = [
    "Superintendent",
    "Principal",
    "Instructional Coach",
    "Reviewer/Auditor",
    "Teacher",
    "Other",
];

const FAQ = [
    {
        q: "Is ESSS a survey tool?",
        a: "No. ESSS is a full assessment platform. Surveys can feed evidence, but the platform generates weighted scoring, audit trails, and board-ready reports across six domains.",
    },
    {
        q: "Can teachers see their own data?",
        a: "Yes. Teachers see their own Instructional Core scores and self-reflection privately. Reviewers and coaches can score them; other teachers cannot see their data.",
    },
    {
        q: "Do we need to migrate our existing data?",
        a: "No. ESSS starts fresh with a baseline cycle. You can optionally import historical benchmarks — talk to us.",
    },
    {
        q: "Is the platform multi-tenant?",
        a: "Yes. Every organization's data is isolated by design, with role-scoped access and per-cycle immutability once locked.",
    },
];

function ContactForm() {
    const [values, setValues] = React.useState({ name: "", email: "", org: "", role: "", message: "" });
    const [errors, setErrors] = React.useState({});
    const [submitting, setSubmitting] = React.useState(false);
    const [success, setSuccess] = React.useState(null);

    function validate() {
        const e = {};
        if (!values.name.trim()) e.name = "Please enter your full name.";
        if (!values.email.trim()) e.email = "Work email is required.";
        else if (!/^\S+@\S+\.\S+$/.test(values.email)) e.email = "Enter a valid email address.";
        if (!values.org.trim()) e.org = "Please tell us your organization.";
        if (!values.role) e.role = "Please pick your role.";
        if (!values.message.trim()) e.message = "A message is required.";
        else if (values.message.trim().length < 20) e.message = "Please give us at least 20 characters.";
        setErrors(e);
        return Object.keys(e).length === 0;
    }

    async function handleSubmit(e) {
        e.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        // Stubbed submission — no backend endpoint, 1s simulated latency
        await new Promise((r) => setTimeout(r, 1000));
        setSubmitting(false);
        setSuccess({ name: values.name.trim(), email: values.email.trim() });
    }

    function resetForm() {
        setValues({ name: "", email: "", org: "", role: "", message: "" });
        setErrors({});
        setSuccess(null);
    }

    if (success) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-[#0E1B2C]"
                data-testid="contact-success-card"
            >
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500 text-white mb-4">
                    <CheckCircle2 size={22} />
                </div>
                <h3 className="font-serif text-2xl font-semibold text-[#0B2545]">
                    Thanks, {success.name}!
                </h3>
                <p className="mt-3 text-[#0E1B2C]/85 leading-relaxed">
                    We've received your message and someone from our team will reply to <strong>{success.email}</strong> within 2 business days.
                </p>
                <button
                    onClick={resetForm}
                    className="mt-4 text-sm text-[#1E56A0] hover:underline font-semibold"
                    data-testid="contact-send-another-btn"
                >
                    Send another message →
                </button>
            </motion.div>
        );
    }

    return (
        <div>
            <div className="text-xs text-[#0E1B2C]/60 italic mb-4" data-testid="contact-demo-notice">
                This form is for demo purposes — production deployments wire it to your CRM or ticketing system.
            </div>
            <form onSubmit={handleSubmit} className="space-y-4" noValidate data-testid="contact-form">
                <div>
                    <Label htmlFor="name">Full Name <span className="text-red-500">*</span></Label>
                    <Input
                        id="name"
                        value={values.name}
                        onChange={(e) => setValues((v) => ({ ...v, name: e.target.value }))}
                        placeholder="Alexandria Rivera"
                        data-testid="contact-input-name"
                    />
                    {errors.name && <div className="text-xs text-red-600 mt-1">{errors.name}</div>}
                </div>
                <div>
                    <Label htmlFor="email">Work Email <span className="text-red-500">*</span></Label>
                    <Input
                        id="email"
                        type="email"
                        value={values.email}
                        onChange={(e) => setValues((v) => ({ ...v, email: e.target.value }))}
                        placeholder="a.rivera@district.edu"
                        data-testid="contact-input-email"
                    />
                    {errors.email && <div className="text-xs text-red-600 mt-1">{errors.email}</div>}
                </div>
                <div>
                    <Label htmlFor="org">Organization / District <span className="text-red-500">*</span></Label>
                    <Input
                        id="org"
                        value={values.org}
                        onChange={(e) => setValues((v) => ({ ...v, org: e.target.value }))}
                        placeholder="Riverside Unified School District"
                        data-testid="contact-input-org"
                    />
                    {errors.org && <div className="text-xs text-red-600 mt-1">{errors.org}</div>}
                </div>
                <div>
                    <Label>Role <span className="text-red-500">*</span></Label>
                    <Select value={values.role} onValueChange={(v) => setValues((prev) => ({ ...prev, role: v }))}>
                        <SelectTrigger data-testid="contact-select-role">
                            <SelectValue placeholder="Pick your role" />
                        </SelectTrigger>
                        <SelectContent>
                            {ROLES.map((r) => (
                                <SelectItem key={r} value={r} data-testid={`contact-role-${r.replace(/[^a-z]/gi, "").toLowerCase()}`}>{r}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {errors.role && <div className="text-xs text-red-600 mt-1">{errors.role}</div>}
                </div>
                <div>
                    <Label htmlFor="message">Message <span className="text-red-500">*</span></Label>
                    <Textarea
                        id="message"
                        value={values.message}
                        onChange={(e) => setValues((v) => ({ ...v, message: e.target.value }))}
                        placeholder="Tell us what you're trying to fix and any timelines we should know about…"
                        rows={6}
                        data-testid="contact-input-message"
                    />
                    {errors.message && <div className="text-xs text-red-600 mt-1">{errors.message}</div>}
                </div>
                <Button
                    type="submit"
                    disabled={submitting}
                    className="rounded-full h-12 px-7 bg-[#0B2545] hover:bg-[#1E56A0] text-white font-semibold"
                    data-testid="contact-submit-btn"
                >
                    {submitting ? <Loader2 className="animate-spin" size={16} /> : <ArrowRight size={16} />}
                    Send Message
                </Button>
            </form>
        </div>
    );
}

function ContactBlock() {
    return (
        <aside className="space-y-4">
            <div className="rounded-2xl border border-[#D6E6F2] bg-white p-6 shadow-sm" data-testid="contact-block-general">
                <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#1E56A0]">Get in touch</div>
                <ul className="mt-4 space-y-3 text-sm text-[#0E1B2C]/90">
                    <li className="flex items-start gap-3"><Mail size={16} className="mt-0.5 text-[#1E56A0]" /> <a href="mailto:hello@esss.example" className="hover:underline">hello@esss.example</a></li>
                    <li className="flex items-start gap-3"><Phone size={16} className="mt-0.5 text-[#1E56A0]" /> +1 (555) 010-ESSS</li>
                    <li className="flex items-start gap-3"><MapPin size={16} className="mt-0.5 text-[#1E56A0]" /> 1200 Framework Way, Suite 400, Austin, TX 78701</li>
                    <li className="flex items-start gap-3"><Clock size={16} className="mt-0.5 text-[#1E56A0]" /> Mon–Fri, 9:00 AM – 5:00 PM CT</li>
                </ul>
            </div>
            <div className="rounded-2xl border border-[#D6E6F2] bg-[#F1F6FB] p-6" data-testid="contact-block-customers">
                <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#1E56A0]">For existing customers</div>
                <div className="mt-4 text-sm text-[#0E1B2C]/90">
                    Support: <a href="mailto:support@esss.example" className="hover:underline text-[#1E56A0] font-semibold">support@esss.example</a>
                </div>
                <Link to="/login" data-testid="contact-signin-btn">
                    <Button className="mt-4 rounded-full bg-[#0B2545] text-white hover:bg-[#1E56A0]">
                        Sign in to your dashboard →
                    </Button>
                </Link>
            </div>
        </aside>
    );
}

function FaqAccordion() {
    const [open, setOpen] = React.useState(null);
    return (
        <div className="space-y-3" data-testid="contact-faq">
            {FAQ.map((item, i) => {
                const isOpen = open === i;
                return (
                    <div key={i} className="rounded-2xl border border-[#D6E6F2] bg-white overflow-hidden" data-testid={`faq-item-${i}`}>
                        <button
                            onClick={() => setOpen(isOpen ? null : i)}
                            className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-[#F1F6FB] transition-colors"
                            data-testid={`faq-toggle-${i}`}
                        >
                            <span className="font-serif text-lg font-semibold text-[#0B2545]">{item.q}</span>
                            <ChevronDown className={`text-[#1E56A0] transition-transform ${isOpen ? "rotate-180" : ""}`} size={18} />
                        </button>
                        <AnimatePresence initial={false}>
                            {isOpen && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="overflow-hidden"
                                >
                                    <div className="px-5 pb-5 text-[#0E1B2C]/85 leading-relaxed" data-testid={`faq-answer-${i}`}>
                                        {item.a}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                );
            })}
        </div>
    );
}

export default function ContactPage() {
    return (
        <div className="min-h-screen bg-white text-[#0E1B2C]" data-testid="contact-page">
            <PublicNav variant="dark" />

            <section
                className="pt-32 pb-16 lg:pt-40 lg:pb-24 px-6 lg:px-10 text-white relative overflow-hidden"
                style={{ background: "linear-gradient(135deg, #0B2545 0%, #1E56A0 60%, #8AC4FF 120%)" }}
                data-testid="contact-hero"
            >
                <motion.div
                    aria-hidden
                    className="absolute -bottom-32 -right-20 h-[420px] w-[420px] rounded-full bg-[#8AC4FF]/25 blur-3xl"
                    animate={{ y: [0, -18, 0] }}
                    transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
                />
                <div className="max-w-7xl mx-auto relative">
                    <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8AC4FF] mb-3">Contact</div>
                    <motion.h1
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight max-w-3xl"
                        data-testid="contact-title"
                    >
                        Let's talk about your district.
                    </motion.h1>
                    <p className="mt-6 max-w-2xl text-lg text-white/85 leading-relaxed">
                        Tell us what you're trying to fix — we'll come back within two business days.
                    </p>
                </div>
            </section>

            <section className="py-16 lg:py-24 px-6 lg:px-10">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-14">
                    <div className="lg:col-span-2">
                        <ContactForm />
                    </div>
                    <ContactBlock />
                </div>
            </section>

            <section className="bg-[#F1F6FB] py-16 lg:py-24 px-6 lg:px-10">
                <div className="max-w-4xl mx-auto">
                    <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-3">Frequently asked</div>
                    <h2 className="font-serif text-3xl sm:text-4xl text-[#0B2545] font-semibold leading-tight mb-10">
                        Quick answers before you write in.
                    </h2>
                    <FaqAccordion />
                </div>
            </section>

            <PublicFooter />
        </div>
    );
}
